import React, { FC } from 'react';

import { Box } from '@rbilabs/component-library';
import delv from 'dlv';
import styled from 'styled-components';

import { ISanityBlockContent } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';
import Picture from 'components/picture';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';
import useEffectOnce from 'hooks/use-effect-once';
import { useCustomizableContent } from 'state/customizable-content';
import { getLocaleImage, getLocaleText, getLocaleTextBlock } from 'utils/sanity';

const Outer = styled.div`
  padding: 20px;
`;

const PictureContainer = styled(Picture)`
  display: flex;
  flex-direction: row;
  height: auto;
  background: ${Styles.color.primary};
  border-radius: 6px;
  ${Styles.desktop`
    height: 300px;
  `}
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  padding: 2rem 1rem;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem 1rem 0;
  justify-content: space-between;
`;

const HeaderBlockContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SideImage = styled(Picture)`
  width: 100%;
`;

const HeaderH2 = styled.h2`
  font-size: 1.75rem;
  color: ${Styles.color.white};
  margin: 0;
  padding: 0.65rem 0 0.8rem 0;
  max-width: 300px;
  line-height: 1;
  ${Styles.desktop`
    font-size: 2rem;
  `}
`;

const HeaderH3 = styled.h3`
  font-size: 1.25rem;
  color: ${Styles.color.white};
  max-width: 300px;
  margin: 0;
  padding: 0;
  line-height: 1;
  ${Styles.desktop`
    font-size: 2rem;
  `}
`;

const HeaderP = styled.p`
  line-height: 1;
  text-align: left;
  color: ${Styles.color.white};
`;

const HeaderStrong = styled.strong`
  color: ${Styles.color.white};
  font-weight: ${Styles.fontWeight.black};
`;

const HeaderEm = styled.em`
  display: block;
  color: ${Styles.color.white};
  font-family: ${Styles.fontFamily.brand};
  font-weight: ${Styles.fontWeight.normal};
  transform: rotate(-5deg) translateY(-10%);
  text-align: left;
`;

const pointBlockContentOverrides: IBlockContentOverrides = {
  normal: HeaderP,
  h2: HeaderH2,
  h3: HeaderH3,
};

const pointBlockSerializers: ISerializers = {
  marks: {
    strong: HeaderStrong,
    em: HeaderEm,
  },
  container: PassthroughComponent,
};

interface ISanityBlockRenderer {
  content?: ISanityBlockContent[] | null;
}

export const HeaderBlock: FC<ISanityBlockRenderer> = ({ content }) => (
  <SanityBlockRenderer
    blockContentOverrides={pointBlockContentOverrides}
    serializers={pointBlockSerializers}
    content={content}
  />
);

export const OnboardingCard = () => {
  const {
    loyaltyContent,
    requestLoyaltyCustomizableContent,
    isLoadingLoyalty,
  } = useCustomizableContent();
  useEffectOnce(requestLoyaltyCustomizableContent);

  const backgroundImage = getLocaleImage(loyaltyContent, 'onboardingContentCardBackgroundImage');
  const sideImage = getLocaleImage(loyaltyContent, 'onboardingImage');
  const headerBlockContent = getLocaleTextBlock(loyaltyContent, 'onboardingTextBlock');
  const buttonText = getLocaleText(loyaltyContent, 'onboardingButtonText');
  const buttonUrl = delv(loyaltyContent, `onboardingButtonLink`);
  return (
    <Outer>
      <PictureContainer image={backgroundImage.image} alt={backgroundImage.alt}>
        {isLoadingLoyalty ? (
          <LoadingContainer>
            <LoadingAnimation fillColor={Styles.color.white} />
          </LoadingContainer>
        ) : (
          <Container>
            <Left>
              {sideImage && sideImage.image && (
                <SideImage objectFitContain image={sideImage.image} alt={sideImage.alt || ''} />
              )}
            </Left>
            <Right>
              <HeaderBlockContainer>
                {headerBlockContent && <HeaderBlock content={headerBlockContent} />}
              </HeaderBlockContainer>
              {buttonText && (
                <Box margin="1.25rem 0 0.5rem">
                  <ActionButton variant={ActionButtonVariants.INVERSE} to={buttonUrl || '/'}>
                    {buttonText}
                  </ActionButton>
                </Box>
              )}
            </Right>
          </Container>
        )}
      </PictureContainer>
    </Outer>
  );
};

export default OnboardingCard;
